/**
 * Case Summary PDF Template
 * Generates a concise case overview for tribunal/audit purposes
 */

import {
  createPDF,
  addHeader,
  addFootersToAllPages,
  addSectionTitle,
  addKeyValue,
  addStatusBadge,
  checkPageBreak,
  addWrappedText,
  generateFilename,
  downloadPDF,
  addTableOfContents,
  addEnhancedProgressBar,
  anonymizeName,
} from '../pdfGenerator';
import {
  colors,
  fontSizes,
  page,
  statusColors,
  priorityColors,
  hexToRgb,
  formatDate,
  formatDateTime,
  getStatusDisplay,
  getPriorityDisplay,
  documentTypes,
  tableStyles,
} from '../pdfStyles';

/**
 * Generate Case Summary PDF
 * @param {Object} caseData - Full case data with stages, actions, notes, documents
 * @param {Object} options - Export options
 * @param {boolean} options.completedOnly - Only include completed actions
 * @param {string} options.exportedBy - User email who exported
 * @param {string} options.tenantName - Company/tenant name
 * @returns {Promise<void>}
 */
export const generateCaseSummaryPDF = async (caseData, options = {}) => {
  const { completedOnly = false, exportedBy = 'System', tenantName = 'Company', anonymize = false, returnBytes = false } = options;

  // Helper to conditionally anonymize names
  const getName = (name) => (anonymize ? anonymizeName(name) : name);

  const doc = createPDF('portrait');
  const headerOptions = {
    documentType: documentTypes.CASE_SUMMARY,
    tenantName,
  };

  let y = addHeader(doc, headerOptions);

  // Document title
  doc.setFontSize(fontSizes.title);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.text));
  doc.text('CASE SUMMARY REPORT', page.marginLeft, y + 5);
  y += 5;

  // Anonymization notice
  if (anonymize) {
    doc.setFontSize(fontSizes.small);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...hexToRgb(colors.warning));
    doc.text('(Names anonymized)', page.marginLeft, y + 5);
  }
  y += 10;

  // ===== TABLE OF CONTENTS =====
  const tocSections = [
    { title: 'Case Information', page: 1 },
    { title: 'Staff Member', page: 1 },
    { title: 'Progress Summary', page: 1 },
    { title: completedOnly ? 'Completed Actions' : 'Actions', page: 2 },
    { title: 'Documents', page: 2 },
    { title: 'Risk Assessment', page: 2 },
  ];
  y = addTableOfContents(doc, tocSections, y, headerOptions);

  // Start new page after TOC
  doc.addPage();
  y = addHeader(doc, headerOptions);

  // ===== CASE INFORMATION =====
  y = addSectionTitle(doc, 'Case Information', y);

  y = addKeyValue(doc, 'Case ID', caseData.case_id, y);
  y = addKeyValue(doc, 'Title', caseData.title, y);
  y = addKeyValue(doc, 'Type', caseData.case_type, y);

  // Status with badge
  doc.setFontSize(fontSizes.body);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.text('Status:', page.marginLeft, y);
  addStatusBadge(doc, getStatusDisplay(caseData.status), page.marginLeft + 40, y, statusColors);
  y += 5;

  // Priority with badge
  doc.setFontSize(fontSizes.body);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.text('Priority:', page.marginLeft, y);
  addStatusBadge(doc, getPriorityDisplay(caseData.priority), page.marginLeft + 40, y, priorityColors);
  y += 5;

  y = addKeyValue(doc, 'Created', formatDate(caseData.actual_created_date || caseData.created_at), y);
  y = addKeyValue(doc, 'Created By', caseData.created_by, y);

  y += 5;

  // ===== STAFF MEMBER =====
  y = checkPageBreak(doc, y, 40, headerOptions);
  y = addSectionTitle(doc, 'Staff Member', y);

  const staff = caseData.staff || {};
  y = addKeyValue(doc, 'Name', getName(staff.full_name) || 'Not Assigned', y);
  y = addKeyValue(doc, 'Employee ID', staff.employee_id || 'N/A', y);
  y = addKeyValue(doc, 'Department', staff.department || 'N/A', y);
  y = addKeyValue(doc, 'Position', staff.position || 'N/A', y);

  y += 5;

  // ===== PROGRESS SUMMARY =====
  y = checkPageBreak(doc, y, 30, headerOptions);
  y = addSectionTitle(doc, 'Progress Summary', y);

  // Calculate progress
  const stages = caseData.case_stages || [];
  const currentStage = stages.find((s) => s.status === 'active') || stages[stages.length - 1];
  const completedStages = stages.filter((s) => s.status === 'completed').length;
  const totalStages = stages.length;

  // Calculate total actions progress
  let totalActions = 0;
  let completedActions = 0;
  stages.forEach((stage) => {
    const actions = stage.case_actions || [];
    totalActions += actions.length;
    completedActions += actions.filter((a) => a.status === 'completed').length;
  });

  y = addKeyValue(
    doc,
    'Current Stage',
    currentStage ? `${currentStage.stage_name} (Stage ${completedStages + 1} of ${totalStages})` : 'No stages',
    y
  );

  // Enhanced progress bars
  y = addEnhancedProgressBar(doc, 'Stages', completedStages, totalStages, y);
  y = addEnhancedProgressBar(doc, 'Actions', completedActions, totalActions, y);

  y += 5;

  // ===== COMPLETED ACTIONS =====
  y = checkPageBreak(doc, y, 40, headerOptions);
  y = addSectionTitle(doc, completedOnly ? 'Completed Actions' : 'Actions', y);

  // Gather all actions from all stages
  let allActions = [];
  stages.forEach((stage) => {
    const actions = stage.case_actions || [];
    actions.forEach((action) => {
      allActions.push({
        ...action,
        stage_name: stage.stage_name,
      });
    });
  });

  // Filter if completedOnly
  if (completedOnly) {
    allActions = allActions.filter((a) => a.status === 'completed');
  }

  // Sort by completion date (oldest first) for chronological order
  allActions.sort((a, b) => {
    const dateA = new Date(a.actual_action_date || a.completed_at || a.created_at);
    const dateB = new Date(b.actual_action_date || b.completed_at || b.created_at);
    return dateA - dateB;
  });

  if (allActions.length === 0) {
    doc.setFontSize(fontSizes.body);
    doc.setTextColor(...hexToRgb(colors.textLight));
    doc.text('No actions recorded.', page.marginLeft, y);
    y += 8;
  } else {
    // Create actions table
    const actionRows = allActions.map((action) => {
      const statusIcon = action.status === 'completed' ? '✓' : '○';
      const legalFlag = action.is_legal ? ' [LEGAL]' : '';
      const acasFlag = action.is_acas ? ' [ACAS]' : '';
      const date = formatDate(action.completed_at || action.actual_action_date || action.created_at);

      return [
        `${statusIcon} ${action.action_name}${legalFlag}${acasFlag}`,
        action.stage_name || '',
        date,
        action.notes || '',
      ];
    });

    doc.autoTable({
      startY: y,
      head: [['Action', 'Stage', 'Date', 'Notes']],
      body: actionRows,
      ...tableStyles.default,
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 35 },
        2: { cellWidth: 25 },
        3: { cellWidth: 60 },
      },
      margin: { left: page.marginLeft, right: page.marginRight },
      didDrawPage: (data) => {
        // Add header on new pages
        if (data.pageNumber > 1) {
          addHeader(doc, headerOptions);
        }
      },
    });

    y = doc.lastAutoTable.finalY + 8;
  }

  // ===== DOCUMENTS =====
  y = checkPageBreak(doc, y, 30, headerOptions);
  y = addSectionTitle(doc, 'Documents', y);

  // Gather documents from all stages
  let allDocuments = [];
  stages.forEach((stage) => {
    const docs = stage.case_documents || [];
    docs.forEach((d) => {
      allDocuments.push({
        ...d,
        stage_name: stage.stage_name,
      });
    });
  });

  // Sort documents by date (oldest first) for chronological order
  allDocuments.sort((a, b) => {
    const dateA = new Date(a.actual_document_date || a.uploaded_at || a.created_at);
    const dateB = new Date(b.actual_document_date || b.uploaded_at || b.created_at);
    return dateA - dateB;
  });

  if (allDocuments.length === 0) {
    doc.setFontSize(fontSizes.body);
    doc.setTextColor(...hexToRgb(colors.textLight));
    doc.text('No documents uploaded.', page.marginLeft, y);
    y += 8;
  } else {
    const docRows = allDocuments.map((document) => [
      `• ${document.document_name || 'Unnamed Document'}`,
      formatDate(document.actual_document_date || document.uploaded_at || document.created_at),
    ]);

    doc.autoTable({
      startY: y,
      head: [['Document', 'Date']],
      body: docRows,
      ...tableStyles.compact,
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 40 },
      },
      margin: { left: page.marginLeft, right: page.marginRight },
    });

    y = doc.lastAutoTable.finalY + 8;
  }

  // ===== RISK ASSESSMENT =====
  y = checkPageBreak(doc, y, 25, headerOptions);
  y = addSectionTitle(doc, 'Risk Assessment', y);

  const tribunalRisk = caseData.tribunal_risk_score || 0;
  const riskLevel = tribunalRisk >= 70 ? 'High' : tribunalRisk >= 40 ? 'Medium' : 'Low';
  const riskColor = tribunalRisk >= 70 ? colors.danger : tribunalRisk >= 40 ? colors.warning : colors.success;

  y = addKeyValue(doc, 'Tribunal Risk', `${riskLevel} (${tribunalRisk}%)`, y, { valueColor: riskColor });

  if (caseData.risk_factors) {
    y = addKeyValue(doc, 'Key Factors', caseData.risk_factors, y);
  }

  y += 5;

  // ===== EXPORT METADATA =====
  y = checkPageBreak(doc, y, 20, headerOptions);
  doc.setDrawColor(...hexToRgb(colors.border));
  doc.setLineWidth(0.5);
  doc.line(page.marginLeft, y, page.contentWidth + page.marginLeft, y);
  y += 5;

  doc.setFontSize(fontSizes.small);
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.text(`Export Type: Case Summary${completedOnly ? ' (Completed Actions Only)' : ''}${anonymize ? ' - Anonymized' : ''}`, page.marginLeft, y);
  y += 4;
  doc.text(`Exported: ${formatDateTime(new Date())}`, page.marginLeft, y);
  y += 4;
  doc.text(`Exported By: ${exportedBy}`, page.marginLeft, y);

  // Add footers to all pages
  addFootersToAllPages(doc, {
    exportedBy,
    exportDate: new Date(),
  });

  // Generate filename
  const filename = generateFilename(caseData.case_id, 'case_summary');

  // Return bytes for merging, or download directly
  if (returnBytes) {
    const pdfBytes = doc.output('arraybuffer');
    return { bytes: new Uint8Array(pdfBytes), filename };
  }

  downloadPDF(doc, filename);
  return filename;
};

export default generateCaseSummaryPDF;
